import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { marketShopAPI } from '@/api/new-api'
import { MarketShopProductModel } from '@/models'

export type MarketShopCartItemRequest = {
  productId: MarketShopProductModel['id']
  quantity: number
  note?: string
}

export type CalculateMarketShopCartRequest = {
  sessionId: string
  cart: {
    items: MarketShopCartItemRequest[]
  }
}

export type MarketShopCartItemResponse = {
  name: string
  price: number
  productId: MarketShopProductModel['id']
  quantity: number
  tax: number
  taxAmount: number
  total: number
}

export type CalculateMarketShopCartResponse = {
  items: MarketShopCartItemResponse[]
  tax: number
  total: number
  discount: number
}

export const getCalculatedMarketShopCartQueryKey = (data: CalculateMarketShopCartRequest) => [
  'useGetCalculatedMarketShopCart',
  data,
]

export const getCalculatedMarketShopCart = async (
  data: CalculateMarketShopCartRequest,
  signal?: AbortSignal,
) => {
  const response = await marketShopAPI.post<CalculateMarketShopCartResponse>(
    `/market-shop/cart`,
    data,
    { signal },
  )
  return response.data
}

export const useGetCalculatedMarketShopCart = (data: CalculateMarketShopCartRequest) => {
  const queryResult = useQuery({
    enabled: !!data.sessionId && !!data.cart.items.length,
    gcTime: 0,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getCalculatedMarketShopCart(data, signal),
    queryKey: getCalculatedMarketShopCartQueryKey(data),
  })

  const totalPrice = queryResult.data?.total || 0
  const taxPrice = queryResult.data?.tax || 0
  const discount = queryResult.data?.discount || 0
  // Note: Subtotal is a sum of initial prices, that's why discount is added back here
  // We want to show to the user an price and then display the discount so he sees the correct calculation
  const subTotalPrice = totalPrice - taxPrice + discount

  const calculatedCartMap: Map<MarketShopProductModel['id'], MarketShopCartItemResponse> =
    useMemo(() => {
      const items = queryResult.data?.items ?? []
      return new Map(items.map((item) => [item.productId, item]))
    }, [queryResult.data])

  return {
    ...queryResult,
    calculatedCartMap,
    discount,
    subTotalPrice,
    taxPrice,
    totalPrice,
  }
}
